import React, { useState, useEffect, useMemo } from "react";
import { Phone, MessageCircle, Youtube, Instagram, Send, ChevronDown, Copy, Check, Mail, Facebook } from "lucide-react";
import { apiClient } from "../../App";
import { toast } from "sonner";

const FloatingContactIcons = () => {
  const [contacts, setContacts] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedItem, setCopiedItem] = useState(null);
  
  useEffect(() => {
    let isMounted = true;
    
    const fetchContacts = async () => {
      try {
        const response = await apiClient.get('/contact/settings');
        if (isMounted && response.data.show_contacts) {
          setContacts(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch contact settings:', err);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    fetchContacts();
    
    return () => {
      isMounted = false;
    };
  }, []);
  
  // Generate contact items with copyable values
  const contactItems = useMemo(() => {
    if (!contacts) return [];
    
    const items = [];
    
    // WhatsApp - show full number for copying
    if (contacts.whatsapp_number) {
      let phone = contacts.whatsapp_number.replace(/\D/g, '');
      if (phone.length === 10) phone = '91' + phone;
      items.push({
        icon: MessageCircle,
        label: 'WhatsApp',
        displayValue: `+${phone}`,
        copyValue: phone,
        color: 'text-green-400',
        bgColor: 'bg-green-500',
      });
    }
    
    // Phone - show full number
    if (contacts.phone_number) {
      items.push({
        icon: Phone,
        label: 'Phone',
        displayValue: contacts.phone_number,
        copyValue: contacts.phone_number,
        color: 'text-blue-400',
        bgColor: 'bg-blue-500',
      });
    }
    
    // Email - show full email
    if (contacts.email_address) {
      items.push({
        icon: Mail,
        label: 'Email',
        displayValue: contacts.email_address,
        copyValue: contacts.email_address,
        color: 'text-orange-400',
        bgColor: 'bg-orange-500',
      });
    }
    
    // Telegram - show link
    if (contacts.telegram_group) {
      items.push({
        icon: Send,
        label: 'Telegram',
        displayValue: contacts.telegram_group,
        copyValue: contacts.telegram_group,
        color: 'text-sky-400',
        bgColor: 'bg-sky-500',
      });
    }
    
    // YouTube - show channel link
    if (contacts.youtube_channel) {
      items.push({
        icon: Youtube,
        label: 'YouTube',
        displayValue: contacts.youtube_channel,
        copyValue: contacts.youtube_channel,
        color: 'text-red-400',
        bgColor: 'bg-red-500',
      });
    }
    
    // Facebook - show page link
    if (contacts.facebook_page) {
      items.push({
        icon: Facebook,
        label: 'Facebook',
        displayValue: contacts.facebook_page,
        copyValue: contacts.facebook_page,
        color: 'text-blue-500',
        bgColor: 'bg-blue-600',
      });
    }
    
    // Instagram - show profile link
    if (contacts.instagram_profile) {
      items.push({
        icon: Instagram,
        label: 'Instagram',
        displayValue: contacts.instagram_profile,
        copyValue: contacts.instagram_profile,
        color: 'text-pink-400',
        bgColor: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400',
      });
    }
    
    return items;
  }, [contacts]);
  
  // Copy to clipboard - works in all browsers
  const copyToClipboard = (text, label) => {
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => {
          setCopiedItem(label);
          toast.success(`${label} copied to clipboard!`, { duration: 2000 });
          setTimeout(() => setCopiedItem(null), 2000);
        })
        .catch(() => {
          // Fallback for clipboard API failure
          fallbackCopy(text, label);
        });
    } else {
      // Fallback for non-secure contexts (http)
      fallbackCopy(text, label);
    }
  };
  
  // Fallback copy using textarea
  const fallbackCopy = (text, label) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopiedItem(label);
        toast.success(`${label} copied to clipboard!`, { duration: 2000 });
        setTimeout(() => setCopiedItem(null), 2000);
      } else {
        toast.error('Copy failed. Please select and copy manually.');
      }
    } catch (err) {
      toast.error('Copy failed. Please select and copy manually.');
    }
    
    document.body.removeChild(textarea);
  };
  
  if (isLoading || !contacts || !contacts.show_contacts || contactItems.length === 0) return null;
  
  return (
    <div className="fixed right-4 top-1/2 -translate-y-1/2 z-40" data-testid="floating-contact-icons">
      {/* Collapsed view - just toggle button */}
      {!isExpanded ? (
        <button
          onClick={() => setIsExpanded(true)}
          className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center shadow-lg hover:scale-110 transition-all duration-200 animate-pulse"
          data-testid="contact-expand-btn"
          title="Contact Us"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      ) : (
        /* Expanded dropdown view */
        <div className="bg-[#0f1419] border border-[#2a3544] rounded-xl shadow-2xl overflow-hidden min-w-[320px] max-w-[400px]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#1a2332] border-b border-[#2a3544]">
            <span className="text-sm font-semibold text-white">Contact Us</span>
            <button
              onClick={() => setIsExpanded(false)}
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#2a3544] transition-colors"
              title="Close"
            >
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          
          {/* Contact Items - Simple list with copy buttons */}
          <div className="p-3 space-y-2">
            {contactItems.map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-3 rounded-lg bg-[#1a2332] hover:bg-[#1f2937] transition-colors"
              >
                {/* Icon */}
                <div className={`w-10 h-10 rounded-full ${item.bgColor} flex items-center justify-center flex-shrink-0`}>
                  <item.icon className="w-5 h-5 text-white" />
                </div>
                
                {/* Info - Label and Value */}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold">{item.label}</div>
                  <div 
                    className={`text-sm font-medium ${item.color} break-all select-all cursor-text`}
                    title="Click to select, then Ctrl+C to copy"
                  >
                    {item.displayValue}
                  </div>
                </div>
                
                {/* Copy Button */}
                <button
                  onClick={() => copyToClipboard(item.copyValue, item.label)}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all ${
                    copiedItem === item.label 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-[#2a3544] hover:bg-cyan-500/20 text-gray-400 hover:text-cyan-400'
                  }`}
                  title={copiedItem === item.label ? 'Copied!' : 'Copy to clipboard'}
                >
                  {copiedItem === item.label ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            ))}
          </div>
          
          {/* Footer hint */}
          <div className="px-4 py-2 bg-[#0a0f14] border-t border-[#2a3544]">
            <p className="text-[10px] text-gray-500 text-center">
              Click <span className="inline-flex items-center bg-[#2a3544] px-1 py-0.5 rounded mx-0.5"><Copy className="w-3 h-3" /></span> to copy or select text manually
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingContactIcons;
